import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { calculateEnvironmentalSummary } from '@/utils/calculations/difficultyScore';
import { calculateExposureMetrics, type ExposureMetrics } from '@/utils/calculations/exposureCalculator';
import { useAirMapStore } from '@/stores/useAirMapStore';
import { renderRouteArt } from '@/utils/routeArt';
import type { RecordingResult, EnvironmentalSummary, CreateActivityData, RunSnapshot } from '@/types/running';

interface PostActivitySummaryProps {
  isOpen: boolean;
  result: RecordingResult | null;
  onClose: () => void;
}

const activityLabels: Record<string, string> = {
  run: 'Run', walk: 'Walk', cycle: 'Ride', hike: 'Hike',
  trail_run: 'Trail Run', yoga: 'Yoga', swim: 'Swim',
  strength: 'Strength', treadmill: 'Treadmill', other: 'Activity',
};

const timeOfDayLabel = () => {
  const h = new Date().getHours();
  if (h < 6) return 'Early Morning';
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  if (h < 21) return 'Evening';
  return 'Night';
};

export function PostActivitySummary({ isOpen, result, onClose }: PostActivitySummaryProps) {
  const { createActivity } = useRunningStore();
  const ingestSamples = useAirMapStore((s) => s.ingestSamples);
  const [title, setTitle] = useState('');
  const [envSummary, setEnvSummary] = useState<EnvironmentalSummary | null>(null);
  const [exposure, setExposure] = useState<ExposureMetrics | null>(null);
  const [routeArtUrl, setRouteArtUrl] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [sharePreview, setSharePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!result || !isOpen) return;

    const label = activityLabels[result.activityType] || 'Activity';
    setTitle(`${timeOfDayLabel()} ${label}`);

    const summary = calculateEnvironmentalSummary({
      aqi_samples: result.aqi_samples,
      elevation_gain: result.elevationGain,
      distance_meters: result.distance,
    });
    setEnvSummary(summary);

    // Calculate breathing exposure metrics
    const exposureMetrics = calculateExposureMetrics(
      result.duration,
      result.distance,
      result.aqi_samples,
      result.snapshots,
      result.activityType,
    );
    setExposure(exposureMetrics);

    if (result.route.length >= 2) {
      const canvas = renderRouteArt(result.route, 600, 300);
      setRouteArtUrl(canvas.toDataURL('image/png'));
    } else {
      setRouteArtUrl(null);
    }

    setSaved(false);
    setUserPhoto(null);
    setSharePreview(null);
  }, [result, isOpen]);

  // Regenerate preview whenever photo or title changes
  useEffect(() => {
    if (!result || !isOpen) return;
    generateSharePreview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPhoto, title, envSummary]);

  const distKm = result ? result.distance / 1000 : 0;
  const paceSeconds = distKm > 0.01 ? result!.duration / distKm : 0;
  const paceMin = Math.floor(paceSeconds / 60);
  const paceSec = Math.floor(paceSeconds % 60);

  const formatDuration = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    return `${m}m ${sec}s`;
  };

  const formatDurationShort = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m ${sec.toString().padStart(2, '0')}s`;
    return `${m}m ${sec.toString().padStart(2, '0')}s`;
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setUserPhoto(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setUserPhoto(null);
    setSharePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /**
   * Build the Strava-style share canvas:
   * Photo bg (or dark bg) → dark overlay → route art → large stats → env data → branding
   */
  const buildShareCanvas = useCallback(async (): Promise<HTMLCanvasElement> => {
    const W = 1080;
    const H = 1920; // 9:16 for Instagram stories
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d')!;

    // 1. Background: photo or dark gradient
    if (userPhoto) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = userPhoto;
      });
      // Cover-crop the photo
      const imgRatio = img.width / img.height;
      const canvasRatio = W / H;
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (imgRatio > canvasRatio) {
        sw = img.height * canvasRatio;
        sx = (img.width - sw) / 2;
      } else {
        sh = img.width / canvasRatio;
        sy = (img.height - sh) / 2;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);

      // Dark overlay for readability
      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.fillRect(0, 0, W, H);
    } else {
      // Dark gradient background
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, '#0d1117');
      grad.addColorStop(0.5, '#111827');
      grad.addColorStop(1, '#0d1117');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
    }

    // 2. Route art overlay (upper portion, transparent bg)
    if (result && result.route.length >= 2) {
      const routeCanvas = renderRouteArt(result.route, W, 700, {
        backgroundColor: null, // transparent
        strokeColor: userPhoto ? '#ff6b35' : '#10b981', // orange on photo (like Strava), emerald on dark
        glowColor: userPhoto ? '#ff9a5c' : '#06b6d4',
        padding: 80,
        lineWidth: 6,
        glowWidth: 16,
      });
      ctx.drawImage(routeCanvas, 0, 200);
    }

    // 3. Stats section (center-bottom area)
    const statsY = 960;

    // Semi-transparent backdrop for stats
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, statsY - 40, W, 560);

    // Title
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(title, 60, statsY + 20);

    // Date
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = '24px system-ui, -apple-system, sans-serif';
    ctx.fillText(
      new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }),
      60, statsY + 60
    );

    const showDistance = ['run', 'walk', 'cycle', 'hike', 'trail_run'].includes(result!.activityType);

    if (showDistance) {
      // Distance — BIG (for running/walking/cycling activities)
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';

      // Distance label
      ctx.font = 'bold 32px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.fillText('KILOMETERS', W / 2, statsY + 130);
      // Distance value - VERY BIG
      ctx.font = 'bold 120px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#10b981';
      ctx.fillText(`${distKm.toFixed(2)}`, W / 2, statsY + 250);

      // Time - smaller below
      ctx.font = '28px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.fillText(formatDurationShort(result!.duration), W / 2, statsY + 310);
    } else {
      // Stationary activity: just time
      ctx.textAlign = 'center';
      ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.fillText('Time', W / 2, statsY + 150);
      ctx.font = 'bold 80px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(formatDurationShort(result!.duration), W / 2, statsY + 240);
    }

    // 4. Environmental data bar
    if (envSummary) {
      const envY = statsY + 380;
      ctx.textAlign = 'center';

      // Environmental items
      const envItems: { label: string; value: string; color: string }[] = [];
      if (envSummary.avg_temperature !== null) {
        envItems.push({ label: 'Temp', value: `${envSummary.avg_temperature.toFixed(0)}\u00B0C`, color: '#22d3ee' });
      }
      if (envSummary.avg_humidity !== null) {
        envItems.push({ label: 'Humidity', value: `${envSummary.avg_humidity.toFixed(0)}%`, color: '#60a5fa' });
      }
      const avgAqi = result!.aqi_samples.length > 0
        ? Math.round(result!.aqi_samples.reduce((a, s) => a + s.aqi, 0) / result!.aqi_samples.length)
        : 0;
      if (avgAqi > 0) {
        const aqiColor = avgAqi <= 50 ? '#34d399' : avgAqi <= 100 ? '#fbbf24' : '#f87171';
        envItems.push({ label: 'AQI', value: `${avgAqi}`, color: aqiColor });
      }
      if (envSummary.sun_exposure_pct > 0) {
        envItems.push({ label: 'Daylight', value: `${envSummary.sun_exposure_pct}%`, color: '#fbbf24' });
      }

      if (envItems.length > 0) {
        const itemWidth = W / envItems.length;
        envItems.forEach((item, i) => {
          const x = itemWidth * i + itemWidth / 2;
          ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
          ctx.fillStyle = item.color;
          ctx.fillText(item.value, x, envY);
          ctx.font = '20px system-ui, -apple-system, sans-serif';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
          ctx.fillText(item.label, x, envY + 30);
        });
      }

      // Difficulty badge
      if (envSummary.difficulty_score > 0) {
        const diffY = envY + 75;
        ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
        const diffColor = envSummary.difficulty_score <= 3 ? '#34d399' : envSummary.difficulty_score <= 6 ? '#fbbf24' : '#f87171';
        ctx.fillStyle = diffColor;
        ctx.textAlign = 'center';
        ctx.fillText(
          `Difficulty ${envSummary.difficulty_score.toFixed(1)}/10  \u2022  Harder than ${envSummary.difficulty_percentile}% globally`,
          W / 2, diffY
        );

        // "Blurp.ie" branding text below difficulty
        ctx.font = 'bold 32px system-ui, -apple-system, sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;
        ctx.fillText('Blurp.ie', W / 2, diffY + 55);
        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }
    }

    // 5. Branding - LARGE WHITE LOGO in middle-right area
    ctx.textAlign = 'center';

    // Load and draw transparent logo - BIG in middle-right area
    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    await new Promise<void>((resolve) => {
      logo.onload = () => {
        // Place logo in middle-right area (where user circled)
        const logoSize = 200; // LARGE & OBVIOUS
        const logoX = W - logoSize - 80; // Right side with margin
        const logoY = (H / 2) - 100; // Middle height

        // Add white glow/shadow for visibility
        ctx.shadowColor = 'rgba(255, 255, 255, 0.9)';
        ctx.shadowBlur = 30;
        ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);

        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;

        resolve();
      };
      logo.onerror = () => resolve(); // Continue even if logo fails to load
      logo.src = '/logo-transparent.png';
    });

    return canvas;
  }, [result, userPhoto, title, envSummary, distKm, paceMin, paceSec, paceSeconds]);

  const generateSharePreview = useCallback(async () => {
    if (!result) return;
    const canvas = await buildShareCanvas();
    setSharePreview(canvas.toDataURL('image/jpeg', 0.9));
  }, [result, buildShareCanvas]);

  const handleSave = useCallback(() => {
    if (!result) return;
    const data: CreateActivityData = {
      title,
      activity_type: result.activityType,
      route_polyline: result.route,
      start_location: result.route.length > 0 ? { lat: result.route[0].lat, lng: result.route[0].lng } : undefined,
      end_location: result.route.length > 0 ? { lat: result.route[result.route.length - 1].lat, lng: result.route[result.route.length - 1].lng } : undefined,
      distance_meters: result.distance,
      duration_seconds: result.duration,
      elevation_gain_meters: result.elevationGain,
      aqi_samples: result.aqi_samples,
      visibility: 'public',
    };
    createActivity(data);

    // Ingest GPS+AQI data into street-level air map (builds proprietary data)
    if (result.aqi_samples.length > 0) {
      ingestSamples(result.aqi_samples);
    }

    setSaved(true);
    setTimeout(() => onClose(), 1500);
  }, [result, title, createActivity, ingestSamples, onClose]);

  const handleShare = useCallback(async () => {
    setIsSharing(true);
    try {
      const canvas = await buildShareCanvas();
      const blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), 'image/png')
      );
      const file = new File([blob], 'blurpie-activity.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: title,
          text: `${title} — ${distKm > 0.01 ? `${distKm.toFixed(1)}km` : formatDuration(result!.duration)} on Blurp.ie`,
          files: [file],
        });
      } else {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `blurpie-${Date.now()}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
      }
    } catch {
      // User cancelled or error
    } finally {
      setIsSharing(false);
    }
  }, [result, title, buildShareCanvas, distKm]);

  const handleExportSnapshot = useCallback(async (snapshot: RunSnapshot, idx: number) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      canvas.width = 1080;
      canvas.height = 1920;

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#0d1117');
      gradient.addColorStop(1, '#161b22');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Logo at top
      const snapLogo = new Image();
      snapLogo.crossOrigin = 'anonymous';
      await new Promise<void>((resolve) => {
        snapLogo.onload = () => {
          const logoSize = 160;
          ctx.drawImage(snapLogo, 540 - logoSize / 2, 60, logoSize, logoSize);
          resolve();
        };
        snapLogo.onerror = () => {
          // Fallback to text if logo fails
          ctx.fillStyle = '#00ffd0';
          ctx.font = 'bold 80px Inter, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('B', 540, 180);
          resolve();
        };
        snapLogo.src = '/logo-transparent.png';
      });

      ctx.textAlign = 'center';

      // Timestamp
      const snapTime = new Date(snapshot.timestamp).toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric'
      });
      ctx.fillStyle = '#8b949e';
      ctx.font = '36px Inter, sans-serif';
      ctx.fillText(snapTime, 540, 280);

      // Large AQI
      const aqiColor = snapshot.aqi <= 50 ? '#34d399' : snapshot.aqi <= 100 ? '#fbbf24' : '#f87171';
      ctx.fillStyle = aqiColor;
      ctx.font = 'bold 280px Inter, sans-serif';
      ctx.fillText(snapshot.aqi.toString(), 540, 600);

      ctx.fillStyle = '#8b949e';
      ctx.font = '48px Inter, sans-serif';
      ctx.fillText('AQI', 540, 680);

      // Stats grid
      const snapDistKm = snapshot.distance / 1000;
      const snapPaceMin = Math.floor(snapshot.pace / 60);
      const snapPaceSec = Math.floor(snapshot.pace % 60);

      const stats = [
        { label: 'Distance', value: `${snapDistKm.toFixed(2)} km`, color: '#ffffff' },
        { label: 'Pace', value: `${snapPaceMin}:${snapPaceSec.toString().padStart(2, '0')} /km`, color: '#ffffff' },
        { label: 'Time', value: `${Math.floor(snapshot.elapsed / 60)}:${(snapshot.elapsed % 60).toString().padStart(2, '0')}`, color: '#ffffff' },
      ];

      let yPos = 850;
      stats.forEach(stat => {
        ctx.fillStyle = stat.color;
        ctx.font = 'bold 72px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(stat.value, 540, yPos);

        ctx.fillStyle = '#6e7681';
        ctx.font = '36px Inter, sans-serif';
        ctx.fillText(stat.label, 540, yPos + 50);

        yPos += 160;
      });

      // Environmental data
      yPos += 60;
      const envData = [];
      if (snapshot.pm25) envData.push(`PM2.5: ${Math.round(snapshot.pm25)} µg/m³`);
      if (snapshot.temperature !== null) envData.push(`${Math.round(snapshot.temperature)}°C`);
      if (snapshot.humidity !== null) envData.push(`${Math.round(snapshot.humidity)}% humidity`);

      ctx.fillStyle = '#8b949e';
      ctx.font = '40px Inter, sans-serif';
      envData.forEach(text => {
        ctx.fillText(text, 540, yPos);
        yPos += 60;
      });

      // Footer
      ctx.fillStyle = '#6e7681';
      ctx.font = '36px Inter, sans-serif';
      ctx.fillText('Better Air, Better Runs', 540, 1800);

      // Convert to blob and share
      const blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), 'image/png')
      );
      const file = new File([blob], `blurpie-snapshot-${idx + 1}.png`, { type: 'image/png' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: `Run Snapshot #${idx + 1}`,
          text: `AQI ${snapshot.aqi} • ${snapDistKm.toFixed(2)}km at ${snapPaceMin}:${snapPaceSec.toString().padStart(2, '0')}/km`,
          files: [file],
        });
      } else {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `blurpie-snapshot-${Date.now()}-${idx + 1}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
      }
    } catch {
      // User cancelled or error
    }
  }, []);

  if (!isOpen || !result) return null;

  const isStationary = ['yoga', 'strength', 'treadmill', 'swim'].includes(result.activityType);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[65] bg-[#0d1117] overflow-y-auto"
      >
        <div ref={cardRef} className="min-h-screen pb-8">
          {/* Close button */}
          <div className="absolute top-4 right-4 z-10">
            <button onClick={onClose} className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Share preview OR route art */}
          {sharePreview ? (
            <div className="relative">
              <img src={sharePreview} alt="Share preview" className="w-full" style={{ maxHeight: '50vh', objectFit: 'cover', objectPosition: 'top' }} />
              <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded text-xs text-white/70">
                Share preview
              </div>
            </div>
          ) : routeArtUrl ? (
            <img src={routeArtUrl} alt="Route" className="w-full aspect-[2/1] object-cover" />
          ) : (
            <div className="w-full aspect-[2/1] bg-[#161b22] flex items-center justify-center">
              <span className="text-gray-500 text-lg">
                {isStationary ? 'Indoor Activity' : 'No route data'}
              </span>
            </div>
          )}

          {/* Add photo button */}
          <div className="px-6 pt-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              className="hidden"
            />
            {!userPhoto ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 bg-white/5 hover:bg-white/10 border border-dashed border-white/20 rounded-xl text-gray-400 text-sm flex items-center justify-center gap-2 transition-colors mb-4"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Add Photo for Share
              </button>
            ) : (
              <button
                onClick={removePhoto}
                className="w-full py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-xl text-red-400 text-sm flex items-center justify-center gap-2 transition-colors mb-4"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Remove Photo
              </button>
            )}
          </div>

          {/* Content */}
          <div className="px-6">
            {/* Title (editable) */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1 mr-4">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="text-2xl font-bold text-white bg-transparent border-b border-transparent focus:border-emerald-500/50 outline-none w-full pb-1 transition-colors"
                />
                <p className="text-gray-500 text-sm mt-1">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>

              {/* Difficulty ring */}
              {envSummary && (
                <div className="flex flex-col items-center">
                  <div className="relative w-16 h-16">
                    <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="15.5" fill="none" stroke="#1e293b" strokeWidth="3" />
                      <circle
                        cx="18" cy="18" r="15.5" fill="none"
                        stroke={envSummary.difficulty_score <= 3 ? '#10b981' : envSummary.difficulty_score <= 6 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="3"
                        strokeDasharray={`${envSummary.difficulty_score * 9.74} 97.4`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">{envSummary.difficulty_score.toFixed(1)}</span>
                    </div>
                  </div>
                  <span className="text-gray-500 text-xs mt-1">difficulty</span>
                </div>
              )}
            </div>

            {/* Main stats */}
            {['run', 'walk', 'cycle', 'hike', 'trail_run'].includes(result.activityType) ? (
              <div className="mb-6">
                {/* Distance - BIG */}
                <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 rounded-2xl p-6 text-center border border-emerald-500/20 mb-4">
                  <div className="text-6xl font-bold text-white mb-2">{distKm.toFixed(2)}</div>
                  <div className="text-sm text-gray-400 uppercase tracking-wider">Kilometers</div>
                </div>
                {/* Time - smaller */}
                <div className="text-center mb-4">
                  <div className="text-lg text-gray-400">{formatDuration(result.duration)}</div>
                </div>
              </div>
            ) : (
              <div className="bg-white/5 rounded-2xl p-6 text-center border border-white/5 mb-6">
                <div className="text-3xl font-bold text-white">{formatDuration(result.duration)}</div>
                <div className="text-sm text-gray-400 mt-1">Total Time</div>
              </div>
            )}

            {/* Environmental conditions */}
            {envSummary && (
              <div className="bg-white/5 rounded-2xl p-4 border border-white/5 mb-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Conditions</h3>
                <div className="grid grid-cols-4 gap-3">
                  {envSummary.avg_temperature !== null && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-cyan-400">{envSummary.avg_temperature.toFixed(0)}&deg;</div>
                      <div className="text-xs text-gray-500">temp</div>
                    </div>
                  )}
                  {envSummary.avg_humidity !== null && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-400">{envSummary.avg_humidity.toFixed(0)}%</div>
                      <div className="text-xs text-gray-500">humidity</div>
                    </div>
                  )}
                  {result.aqi_samples.length > 0 && (
                    <div className="text-center">
                      <div className={`text-lg font-bold ${
                        (() => {
                          const a = Math.round(result.aqi_samples.reduce((acc, s) => acc + s.aqi, 0) / result.aqi_samples.length);
                          return a <= 50 ? 'text-emerald-400' : a <= 100 ? 'text-yellow-400' : 'text-red-400';
                        })()
                      }`}>
                        {Math.round(result.aqi_samples.reduce((a, s) => a + s.aqi, 0) / result.aqi_samples.length)}
                      </div>
                      <div className="text-xs text-gray-500">AQI</div>
                    </div>
                  )}
                  {envSummary.sun_exposure_pct > 0 && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-amber-400">{envSummary.sun_exposure_pct}%</div>
                      <div className="text-xs text-gray-500">daylight</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Breathing Exposure - Blurp.ie Unique */}
            {exposure && (
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-4 border border-purple-500/20 mb-4">
                <h3 className="text-xs font-semibold text-purple-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  Breathing Exposure
                </h3>

                {/* Main exposure stat */}
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex-1">
                    <div className="text-3xl font-bold text-white">{exposure.totalPM25Inhaled.toFixed(1)}</div>
                    <div className="text-xs text-gray-400">µg PM2.5 inhaled</div>
                  </div>
                  <div className="flex-1 text-right">
                    <div className={`text-lg font-bold ${
                      exposure.healthImpact === 'minimal' ? 'text-emerald-400' :
                      exposure.healthImpact === 'low' ? 'text-green-400' :
                      exposure.healthImpact === 'moderate' ? 'text-yellow-400' :
                      exposure.healthImpact === 'high' ? 'text-orange-400' : 'text-red-400'
                    }`}>
                      {exposure.healthImpact.charAt(0).toUpperCase() + exposure.healthImpact.slice(1)}
                    </div>
                    <div className="text-xs text-gray-400">health impact</div>
                  </div>
                </div>

                {/* Breathing stats grid */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="bg-white/5 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-cyan-400">{exposure.averageBreathingRate}</div>
                    <div className="text-[10px] text-gray-500">L/min breathed</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-blue-400">{(exposure.totalAirVolume / 1000).toFixed(0)}</div>
                    <div className="text-[10px] text-gray-500">m³ total air</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-amber-400">{exposure.restEquivalentHours}h</div>
                    <div className="text-[10px] text-gray-500">rest equivalent</div>
                  </div>
                </div>

                {/* Comparison text */}
                <p className="text-xs text-gray-400 leading-relaxed">
                  {exposure.comparisonText}
                </p>

                {/* Per-km exposure bars */}
                {exposure.perKmExposure.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Exposure per km</div>
                    <div className="flex gap-1 items-end h-12">
                      {exposure.perKmExposure.map((km, i) => {
                        const maxDose = Math.max(...exposure.perKmExposure.map(k => k.dose));
                        const height = maxDose > 0 ? (km.dose / maxDose) * 100 : 50;
                        const color = km.quality === 'excellent' ? 'bg-emerald-400'
                          : km.quality === 'good' ? 'bg-green-400'
                          : km.quality === 'moderate' ? 'bg-yellow-400'
                          : km.quality === 'poor' ? 'bg-orange-400'
                          : 'bg-red-400';
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                            <div
                              className={`w-full rounded-sm ${color}`}
                              style={{ height: `${Math.max(8, height)}%` }}
                              title={`Km ${km.km}: ${km.dose.toFixed(2)} µg PM2.5`}
                            />
                            <span className="text-[8px] text-gray-600">{km.km}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Difficulty banner */}
            {envSummary && envSummary.difficulty_score > 0 && (
              <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-2xl p-4 border border-emerald-500/20 mb-6">
                <div className="text-white font-semibold mb-1">
                  Harder than {envSummary.difficulty_percentile}% of activities globally
                </div>
                {envSummary.difficulty_factors.length > 0 && (
                  <div className="text-sm text-gray-400">
                    Factors: {envSummary.difficulty_factors.map(f => f.label).join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* Snapshots */}
            {result.snapshots && result.snapshots.length > 0 && (
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Snapshots ({result.snapshots.length})
                </h3>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {result.snapshots.map((snapshot, idx) => {
                    const snapDistKm = snapshot.distance / 1000;
                    const snapPaceMin = Math.floor(snapshot.pace / 60);
                    const snapPaceSec = Math.floor(snapshot.pace % 60);
                    const snapTime = new Date(snapshot.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

                    return (
                      <div key={idx} className="flex-shrink-0 w-64 bg-white/5 border border-white/10 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-xs text-gray-400">{snapTime}</div>
                          <div className="text-xs text-cyan-400 font-mono">
                            {Math.floor(snapshot.elapsed / 60)}:{(snapshot.elapsed % 60).toString().padStart(2, '0')}
                          </div>
                        </div>

                        {/* Air Quality */}
                        <div className="mb-3">
                          <div className={`text-3xl font-bold ${
                            snapshot.aqi <= 50 ? 'text-emerald-400' :
                            snapshot.aqi <= 100 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {snapshot.aqi}
                          </div>
                          <div className="text-xs text-gray-500">AQI</div>
                        </div>

                        {/* Running stats */}
                        <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                          <div>
                            <div className="text-white font-semibold">{snapDistKm.toFixed(2)}</div>
                            <div className="text-gray-500">km</div>
                          </div>
                          <div>
                            <div className="text-white font-semibold">{snapPaceMin}:{snapPaceSec.toString().padStart(2, '0')}</div>
                            <div className="text-gray-500">/km</div>
                          </div>
                          <div>
                            <div className="text-white font-semibold">{snapshot.pm25 ? Math.round(snapshot.pm25) : '--'}</div>
                            <div className="text-gray-500">PM2.5</div>
                          </div>
                        </div>

                        {/* Environmental stats */}
                        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                          {snapshot.temperature !== null && (
                            <div>
                              <div className="text-cyan-400 font-semibold">{Math.round(snapshot.temperature)}°C</div>
                              <div className="text-gray-500">temp</div>
                            </div>
                          )}
                          {snapshot.humidity !== null && (
                            <div>
                              <div className="text-blue-400 font-semibold">{Math.round(snapshot.humidity)}%</div>
                              <div className="text-gray-500">humidity</div>
                            </div>
                          )}
                        </div>

                        {/* Export button */}
                        <button
                          onClick={() => handleExportSnapshot(snapshot, idx)}
                          className="w-full py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/50 rounded-lg text-cyan-400 text-xs font-semibold transition-colors flex items-center justify-center gap-1"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                          Export
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 mb-8">
              <button
                onClick={handleShare}
                disabled={isSharing}
                className="flex-1 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                {isSharing ? 'Sharing...' : 'Share'}
              </button>
              <button
                onClick={handleSave}
                disabled={saved}
                className="flex-1 py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 rounded-xl font-bold text-black transition-all disabled:opacity-50"
              >
                {saved ? 'Saved!' : 'Save Activity'}
              </button>
            </div>

            {/* Logo and branding */}
            <div className="flex flex-col items-center py-6 border-t border-white/5">
              <img src="/logo.png" alt="Blurp.ie" className="w-20 h-20 mb-3" />
              <div className="text-white font-bold text-2xl">Blurp.ie</div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
